import { useTranslation } from 'react-i18next';
import { Button, Divider, Group, List, Skeleton, Stack, Text, ThemeIcon } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconCircleCheck, IconClock, IconDownload, IconShieldCheck, IconX } from '@tabler/icons-react';
import { useAbsentDocuments, useAttest, useDownloadVerificationZip, useNotAttest } from '@/hooks/useDeals';
import type { BargainStepsInfo } from '@/types/deals';
import { formatOrgName } from '@/lib/format';
import IdText from '@/components/common/IdText';

interface AttestationStepProps {
  readonly dealId: number;
  readonly info: BargainStepsInfo;
}

// Шаг «Аттестация» (WAIT_ATTESTATION). Покупатель проверяет документы продавца и решает
// аттестовать/не аттестовать (отмена сделки — необратимо). Продавец/агент — ожидают.
export default function AttestationStep({ dealId, info }: AttestationStepProps) {
  const { t } = useTranslation();
  const seller = info.seller;
  const zip = useDownloadVerificationZip();
  const attest = useAttest(dealId);
  const notAttest = useNotAttest(dealId);
  const absent = useAbsentDocuments(info.isBuyer ? seller?.userId : null, dealId);

  const acting = attest.isPending || notAttest.isPending;

  function confirmNotAttest(): void {
    modals.openConfirmModal({
      title: t('app.deals.attestation.notAttestTitle'),
      children: <Text size="sm">{t('app.deals.attestation.notAttestConfirm')}</Text>,
      labels: { confirm: t('app.deals.attestation.notAttestConfirmBtn'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => notAttest.mutate(),
    });
  }

  // ── Продавец / Агент — ожидание ──────────────────────────────────────────────
  if (!info.isBuyer) {
    return (
      <Stack gap="sm" align="flex-start">
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon size={36} radius="xl" variant="light" color="brand">
            <IconClock size={20} />
          </ThemeIcon>
          <Text fz="sm">{t(info.isSeller ? 'app.deals.attestation.waiting' : 'app.deals.attestation.waitingAgent')}</Text>
        </Group>
        {info.isSeller ? (
          <Button
            variant="default"
            leftSection={<IconDownload size={16} />}
            loading={zip.isPending}
            onClick={() => zip.mutate({ dealId, isSeller: true })}
          >
            {t('app.deals.attestation.downloadOwn')}
          </Button>
        ) : null}
      </Stack>
    );
  }

  // ── Покупатель — решение ──────────────────────────────────────────────────────
  return (
    <Stack gap="md">
      <Text fz="sm" c="gray.8">
        {t('app.deals.attestation.intro')}
      </Text>

      <Stack gap={2}>
        <Text fz="xs" c="gray.7">
          {t('app.deals.summary.seller')}
        </Text>
        <Group gap="xs" wrap="wrap">
          <Text fz="sm" fw={500}>
            {formatOrgName(seller?.name) ?? '—'}
          </Text>
          {seller?.inn ? (
            <Text fz="sm" c="gray.7">
              {t('auth.fields.inn')} <IdText>{seller.inn}</IdText>
            </Text>
          ) : null}
        </Group>
      </Stack>

      <Button
        variant="default"
        leftSection={<IconDownload size={16} />}
        loading={zip.isPending}
        onClick={() => zip.mutate({ dealId, isSeller: true })}
        w="fit-content"
      >
        {t('app.deals.attestation.downloadPackage')}
      </Button>

      {/* Список недостающих документов — совещательный: при ошибке не блокируем решение. */}
      {absent.isLoading ? (
        <Skeleton h={48} />
      ) : absent.isError ? (
        <Text fz="xs" c="gray.6">
          {t('app.deals.attestation.absentError')}
        </Text>
      ) : (absent.data?.fileNames?.length ?? 0) > 0 ? (
        <Stack gap={4}>
          <Text fz="sm" fw={500} c="red.7">
            {t('app.deals.attestation.missingTitle')}
          </Text>
          <List size="sm" spacing={2} c="gray.8">
            {absent.data!.fileNames!.map((name) => (
              <List.Item key={name}>{name}</List.Item>
            ))}
          </List>
        </Stack>
      ) : (
        <Group gap="xs" wrap="nowrap">
          <ThemeIcon size={20} radius="xl" variant="light" color="lime">
            <IconCircleCheck size={14} />
          </ThemeIcon>
          <Text fz="sm" c="gray.8">
            {t('app.deals.attestation.allProvided')}
          </Text>
        </Group>
      )}

      <Divider />

      <Group gap="sm" wrap="wrap">
        <Button
          color="red"
          variant="light"
          c="red.7"
          leftSection={<IconX size={16} />}
          disabled={acting}
          loading={notAttest.isPending}
          onClick={confirmNotAttest}
        >
          {t('app.deals.attestation.notAttest')}
        </Button>
        <Button
          leftSection={<IconShieldCheck size={16} />}
          disabled={acting}
          loading={attest.isPending}
          onClick={() => attest.mutate()}
        >
          {t('app.deals.attestation.attest')}
        </Button>
      </Group>
    </Stack>
  );
}
